/**
 * @fileoverview Backtest slice for the application store
 * This slice manages all backtest-related state including preview results,
 * optimization results, form data, and async operation states.
 */

import type { StateCreator } from 'zustand';
import { notifyCreditChange } from '../../hooks/useCredits';
import type { 
  BacktestSlice, 
  BacktestResult, 
  OptimizationProgress, 
  SavedBacktest,
  BacktestFormData,
  AsyncState 
} from '../../types/store';
import { backtestService, type ApiTypes } from '../../services';
import { optimizationPoller } from '../../services/backtest/optimizationPoller';
import { getBacktestExecutionConfig } from '../configurationStore';

const createInitialAsyncState = (): AsyncState => ({
  data: null,
  loading: false,
  error: null,
});

const createInitialFormData = (): BacktestFormData => ({
  symbol: 'BTCUSDT',
  interval: '1d',
  strategy: 'tdr',
  portfolioAmount: 10000,
  portfolioCurrency: 'USDT',
  params: {},
});

export const createBacktestSlice: StateCreator<BacktestSlice> = (set, get) => ({
  // Initial state
  previewResult: null,
  isPreviewFromOptimization: false, // Flag to prevent auto-save of optimization previews
  optimizationResults: null,
  savedBacktests: [],
  formData: createInitialFormData(),
  backtestName: '',
  
  // Optimization progress tracking
  optimizationProgress: {
    current: 0,
    total: 0,
    isRunning: false,
  },
  
  // Async states
  previewState: createInitialAsyncState(),
  optimizationState: createInitialAsyncState(),
  saveState: createInitialAsyncState(),
  backtestListState: { ...createInitialAsyncState(), data: [] },
  
  // State setters
  setPreviewResult: (result: BacktestResult | null, isFromOptimization?: boolean) => {
    set({ 
      previewResult: result,
      isPreviewFromOptimization: isFromOptimization ?? false,
    });
  },
  
  setOptimizationResults: (results: OptimizationProgress | null) => {
    set({ optimizationResults: results });
  },
  
  setOptimizationProgress: (progress: { current: number; total: number; isRunning: boolean }) => {
    set({ optimizationProgress: progress });
  },
  
  setSavedBacktests: (backtests: SavedBacktest[]) => {
    set({ savedBacktests: backtests });
  },
  
  setFormData: (data: Partial<BacktestFormData>) => {
    set((state) => ({
      formData: { ...state.formData, ...data }
    }));
  },
  
  setBacktestName: (name: string) => {
    set({ backtestName: name });
  },
  
  // Async state setters
  setPreviewState: (newState: Partial<AsyncState>) => {
    set((state) => ({
      previewState: { ...state.previewState, ...newState }
    }));
  },
  
  setOptimizationState: (newState: Partial<AsyncState>) => {
    set((state) => ({
      optimizationState: { ...state.optimizationState, ...newState }
    }));
  },
  
  setSaveState: (newState: Partial<AsyncState>) => {
    set((state) => ({
      saveState: { ...state.saveState, ...newState }
    }));
  },
  
  setBacktestListState: (newState: Partial<AsyncState<SavedBacktest[]>>) => {
    set((state) => ({
      backtestListState: { ...state.backtestListState, ...newState }
    }));
  },
  
  // Reset functions
  resetPreview: () => {
    set({
      previewResult: null,
      isPreviewFromOptimization: false,
      previewState: createInitialAsyncState(),
    });
  },
  
  resetOptimization: () => {
    set({
      optimizationResults: null,
      optimizationState: createInitialAsyncState(),
    });
  },
  
  resetAll: () => {
    set({
      previewResult: null,
      optimizationResults: null,
      formData: createInitialFormData(),
      backtestName: '',
      previewState: createInitialAsyncState(),
      optimizationState: createInitialAsyncState(),
      saveState: createInitialAsyncState(),
    });
  },

  // Async actions
  runPreview: async (
    symbol: string,
    interval: string,
    strategy: string,
    portfolioAmount: number,
    portfolioCurrency: string,
    params: { [key: string]: number | string },
    shortSellingMode?: 'REINVEST_PROFITS' | 'FIXED_AMOUNT' | 'CAPPED_AMOUNT',
    portfolioType?: 'USD' | 'ASSET' | 'MIXED',
    capPercent?: number,
    feePercent?: number
  ) => {
    const { setPreviewState, setPreviewResult } = get();
    
    setPreviewState({ loading: true, error: null });

    try {
      // Extract trading mode, date, and candle range parameters from params
      const { startDate, endDate, startCandle, endCandle, tradingMode, ...strategyParams } = params;
      
      // Determine trading mode - let backend auto-detect when shortSellingMode is enabled
      const shouldAutoDetect = !tradingMode && shortSellingMode && 
        (shortSellingMode === 'REINVEST_PROFITS' || shortSellingMode === 'FIXED_AMOUNT' || shortSellingMode === 'CAPPED_AMOUNT');
      
      const requestBase: ApiTypes.BacktestPreviewRequest = {
        symbol,
        interval,
        strategy,
        portfolioAmount,
        portfolioCurrency,
        portfolioType,
        params: strategyParams,
        shortSellingMode: shortSellingMode || 'REINVEST_PROFITS', // Phase 6 Enhancement: Add shortSellingMode support
        capPercent,
        feePercent, // Custom trading fee percentage
        // Exit management - get from configuration store
        stopLossPercent: getBacktestExecutionConfig().stopLossPercent,
        takeProfitPercent: getBacktestExecutionConfig().takeProfitPercent,
        trailingStopPercent: getBacktestExecutionConfig().trailingStopPercent,
        slippagePercent: getBacktestExecutionConfig().slippagePercent,
      };

      // Only add tradingMode if not auto-detecting
      const request = shouldAutoDetect ? requestBase : {
        ...requestBase,
        tradingMode: (tradingMode as 'LONG_ONLY' | 'SHORT_ONLY' | 'BIDIRECTIONAL') || 'LONG_ONLY'
      };
      
      // Add date parameters if they exist
      if (startDate) {
        request.startDate = String(startDate);
      }
      if (endDate) {
        request.endDate = String(endDate);
      }
      
      // Add candle range parameters if they exist
      if (startCandle !== undefined) {
        request.startCandle = Number(startCandle);
      }
      if (endCandle !== undefined) {
        request.endCandle = Number(endCandle);
      }

      // Use the service instead of direct fetch
      const result = await backtestService.runPreview(request);
      
      // Add the original params back to the result for display purposes
      // This includes both manual parameters and any date range parameters
      const previewResult: BacktestResult = {
        ...result,
        parameters: params,
      };
      
      setPreviewResult(previewResult);
      setPreviewState({ loading: false, data: previewResult });
      notifyCreditChange();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      setPreviewState({ loading: false, error: errorMessage });
    }
  },

  runOptimization: async (
    symbol: string,
    interval: string,
    strategy: string,
    portfolioAmount: number,
    portfolioCurrency: string,
    parameterRanges: { [key: string]: { current: [number, number]; step: number; fixed?: number } },
    optimizationTarget: string,
    startDate?: string,
    endDate?: string,
    tradingMode?: 'LONG_ONLY' | 'SHORT_ONLY' | 'BIDIRECTIONAL'
  ) => {
    const { setOptimizationState } = get();
    
    setOptimizationState({ loading: true, error: null });

    try {
      const apiRanges: {
        [key: string]: { min: number; max: number; step: number };
      } = {};
      Object.entries(parameterRanges).forEach(([param, config]) => {
        // Use the current range [min, max] for optimization
        // Only treat as fixed if current[0] === current[1] (user wants single value)
        const isFixed = config.current[0] === config.current[1];
        
        if (isFixed) {
          // User has set min === max, so use single value
          apiRanges[param] = {
            min: config.current[0],
            max: config.current[0],
            step: 1,
          };
        } else {
          // User wants to optimize this parameter over a range
          apiRanges[param] = {
            min: config.current[0],
            max: config.current[1],
            step: config.step,
          };
        }
      });

  // Get configuration snapshot from centralized helper to ensure consistency
  const configStore = getBacktestExecutionConfig();
  const { shortSellingMode, capPercent, tradingFeePercent } = configStore;

      const request: ApiTypes.OptimizationRequest = {
        symbol,
        interval,
        strategy,
        portfolioAmount,
        portfolioCurrency,
        parameterRanges: apiRanges,
        optimizationTarget,
        tradingMode,
        // Include all configuration parameters for consistency
        shortSellingMode,
        capPercent,
        feePercent: tradingFeePercent,
        limit: 1000,
        enableProgressUpdates: true, // Force async mode for SSE
      };

      // Add date range parameters if provided
      if (startDate && endDate) {
        request.startDate = startDate;
        request.endDate = endDate;
      }

      // Start optimization using service
      const optimizationResponse = await backtestService.startOptimization(request);
      
      // Store the optimization ID for tracking
      if (optimizationResponse.optimizationId) {
        // Set loading state to false - the SSE system will handle progress updates
        setOptimizationState({ loading: false, error: null });
      } else {
        // Sync mode - results are already in the response
        // Cast the response to include results for sync mode
        const syncResponse = optimizationResponse as any;
        if (syncResponse.results || syncResponse.data) {
          get().setOptimizationResults(syncResponse.results || syncResponse.data);
          setOptimizationState({ loading: false, error: null });
        } else {
          setOptimizationState({ loading: false, error: 'No results in synchronous response' });
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      setOptimizationState({ loading: false, error: errorMessage });
    }
  },

  // New method for the optimization queue system
  runOptimizationWithQueue: async (
    symbol: string,
    interval: string,
    strategy: string,
    portfolioAmount: number,
    portfolioCurrency: string,
    parameterRanges: { [key: string]: { current: [number, number]; step: number; fixed?: number } },
    optimizationTarget: string,
    startDate?: string,
    endDate?: string,
    tradingMode?: 'LONG_ONLY' | 'SHORT_ONLY' | 'BIDIRECTIONAL'
  ): Promise<string> => {
    const { setOptimizationState } = get();
    
    try {
      const apiRanges: {
        [key: string]: { min: number; max: number; step: number };
      } = {};
      Object.entries(parameterRanges).forEach(([param, config]) => {
        // Use the current range [min, max] for optimization
        // Only treat as fixed if current[0] === current[1] (user wants single value)
        const isFixed = config.current[0] === config.current[1];
        
        if (isFixed) {
          // User has set min === max, so use single value
          apiRanges[param] = {
            min: config.current[0],
            max: config.current[0],
            step: 1,
          };
        } else {
          // User wants to optimize this parameter over a range
          apiRanges[param] = {
            min: config.current[0],
            max: config.current[1],
            step: config.step,
          };
        }
      });

      const request: ApiTypes.OptimizationRequest = {
        symbol,
        interval,
        strategy,
        portfolioAmount,
        portfolioCurrency,
        parameterRanges: apiRanges,
        optimizationTarget,
        tradingMode,
        limit: 1000,
        enableProgressUpdates: true, // Force async mode for SSE
      };

      // Add date range parameters if provided
      if (startDate && endDate) {
        request.startDate = startDate;
        request.endDate = endDate;
      }

      // Start optimization using service
      const optimizationResponse = await backtestService.startOptimization(request);
      
      if (!optimizationResponse.optimizationId) {
        throw new Error('No optimization ID returned from server');
      }
      
      return optimizationResponse.optimizationId;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      setOptimizationState({ loading: false, error: errorMessage });
      throw error;
    }
  },

  pollForResults: async (optimizationId: string) => {
    const { setOptimizationState, setOptimizationResults, setOptimizationProgress } = get();

    // Set initial progress state
    setOptimizationProgress({ current: 0, total: 1600, isRunning: true });

    try {
      await optimizationPoller.pollOptimization(
        optimizationId,
        // Progress callback
        (progress) => {
          setOptimizationProgress({ 
            current: progress.current, 
            total: progress.total, 
            isRunning: !progress.completed 
          });
          
          if (progress.result) {
            setOptimizationResults(progress.result);
          }
        },
        // Complete callback
        (result) => {
          setOptimizationResults(result);
          setOptimizationProgress({ current: 1600, total: 1600, isRunning: false });
          setOptimizationState({ loading: false, data: result });
        },
        // Error callback
        (error) => {
          setOptimizationState({ 
            loading: false, 
            error: `Optimization polling failed: ${error}` 
          });
          setOptimizationProgress({ current: 0, total: 1600, isRunning: false });
        }
      );
    } catch (error) {
      setOptimizationState({ 
        loading: false, 
        error: error instanceof Error ? error.message : 'Failed to start optimization polling' 
      });
      setOptimizationProgress({ current: 0, total: 1600, isRunning: false });
    }
  },

  saveBacktest: async (
    result: BacktestResult,
    symbol: string,
    interval: string,
    strategy: string,
    portfolioAmount: number,
    portfolioCurrency: string,
    backtestName: string,
    startDate?: string,
    endDate?: string,
    isShared?: boolean,
    tradingMode?: 'LONG_ONLY' | 'SHORT_ONLY' | 'BIDIRECTIONAL'
  ) => {
    const { setSaveState } = get();
    
    if (!backtestName.trim()) {
      setSaveState({ loading: false, error: 'Please enter a backtest name' });
      return { success: false, error: 'Please enter a backtest name' };
    }

    setSaveState({ loading: true, error: null });

    try {
  // Get position sizing configuration from centralized helper
  const configStore = getBacktestExecutionConfig();
  const { shortSellingMode, capPercent } = configStore;
      
      // Include date range parameters, position sizing, and trading mode in the saved params
      const params: Record<string, any> = { 
        ...result.parameters,
        shortSellingMode,
        capPercent,
        tradingMode: tradingMode || 'LONG_ONLY' // Include trading mode (default to LONG_ONLY)
      };
      
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }

      const request: ApiTypes.BacktestSaveRequest = {
        name: backtestName,
        symbol,
        interval,
        strategy,
        portfolioAmount,
        portfolioCurrency,
        params,
        signals: result.signals,
        trades: result.trades,
        stats: result.stats,
        isShared,
      };

      // Use the service instead of direct fetch
      const data = await backtestService.saveBacktest(request);

      setSaveState({ loading: false, data: data });
      return { success: true, data };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      setSaveState({ loading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },
});
